import React, { useState, useRef } from 'react';
import { X, Loader2, Camera, Trash2 } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { CATEGORY_LABELS, THEME_COLORS, type WorkgroupCategory, type ThemeColor } from './types';
import BadgePicker, { WorkgroupBadge } from './BadgePicker';
import type { BadgeIconName } from './badge-catalog';

interface Props {
  onClose: () => void;
  onCreated: (workgroupId: string) => void;
}

interface CreateResp { id: string }
interface AvatarResp { avatar_url: string }

export default function CreateWorkgroupModal({ onClose, onCreated }: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionMd, setDescriptionMd] = useState('');
  const [category, setCategory] = useState<WorkgroupCategory | ''>('');
  const [themeColor, setThemeColor] = useState<ThemeColor>('blue');
  const [badgeIcon, setBadgeIcon] = useState<BadgeIconName | null>(null);
  const [createBoard, setCreateBoard] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  function pickAvatar(file: File) {
    if (!file.type.startsWith('image/')) {
      setErr('Avatar: il file deve essere un\'immagine');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      setErr('Avatar: massimo 25MB');
      return;
    }
    setErr(null);
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  }

  function clearAvatar() {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(null);
    setAvatarPreview(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setErr(null);
    try {
      const res = await apiClient.post<CreateResp>('/messenger/workgroups', {
        name: name.trim(),
        description: description.trim() || undefined,
        description_md: descriptionMd.trim() || undefined,
        category: category || undefined,
        theme_color: themeColor,
        badge_icon: badgeIcon || undefined,
        create_board: createBoard,
      });
      const id = res.data?.id;
      if (!id) throw new Error('Risposta server non valida');

      if (avatarFile) {
        try {
          const fd = new FormData();
          fd.append('file', avatarFile);
          await apiClient.post<AvatarResp>(`/messenger/workgroups/${id}/avatar`, fd);
        } catch (uploadErr) {
          const msg = uploadErr instanceof Error ? uploadErr.message : 'Errore upload';
          setErr(`Workgroup creato, ma upload avatar fallito: ${msg}`);
        }
      }
      onCreated(id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore creazione');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
        <header className="flex items-center justify-between p-5 border-b border-border shrink-0">
          <h2 className="text-lg font-semibold text-foreground">Nuovo workgroup</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={20} />
          </button>
        </header>

        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Avatar + badge in alto */}
          <div className="flex items-start gap-3 p-3 rounded-xl border border-border bg-muted/30">
            <div className="relative shrink-0">
              <div className="w-16 h-16 rounded-xl bg-muted border border-border flex items-center justify-center text-foreground text-base font-semibold overflow-hidden">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span aria-hidden>{(name.substring(0, 2) || '··').toUpperCase()}</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                aria-label="Carica avatar"
                className="absolute -bottom-1 -right-1 bg-primary hover:bg-primary/90 w-7 h-7 rounded-full flex items-center justify-center ring-2 ring-card"
              >
                <Camera size={12} className="text-primary-foreground" />
              </button>
              {avatarPreview && (
                <button
                  type="button"
                  onClick={clearAvatar}
                  aria-label="Rimuovi avatar"
                  className="absolute -top-1 -right-1 bg-destructive hover:bg-destructive/90 w-5 h-5 rounded-full flex items-center justify-center ring-2 ring-card"
                >
                  <Trash2 size={10} className="text-destructive-foreground" />
                </button>
              )}
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) pickAvatar(f);
                  e.target.value = '';
                }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs uppercase tracking-wide text-muted-foreground">Badge identificativo</label>
                {badgeIcon && <WorkgroupBadge name={badgeIcon} themeColor={themeColor} size={22} />}
              </div>
              <BadgePicker value={badgeIcon} onChange={setBadgeIcon} themeColor={themeColor} size="sm" />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">Nome *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              required
              autoFocus
              className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-foreground"
              placeholder="Es. Lancio prodotto Q3"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">Categoria</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as WorkgroupCategory | '')}
                className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-foreground"
              >
                <option value="">— nessuna —</option>
                {(Object.keys(CATEGORY_LABELS) as WorkgroupCategory[]).map(c => (
                  <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">Colore</label>
              <div className="flex flex-wrap gap-2 pt-1">
                {THEME_COLORS.map(c => (
                  <button
                    type="button"
                    key={c}
                    onClick={() => setThemeColor(c)}
                    className={`w-6 h-6 rounded-full bg-${c}-500 ring-2 ${themeColor === c ? 'ring-foreground' : 'ring-transparent'}`}
                    aria-label={c}
                  />
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">Descrizione breve</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-foreground"
              placeholder="Una riga: scopo del gruppo"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">Brief esteso (markdown)</label>
            <textarea
              value={descriptionMd}
              onChange={(e) => setDescriptionMd(e.target.value)}
              maxLength={8000}
              rows={4}
              className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-foreground text-sm font-mono"
              placeholder="# Obiettivi&#10;- ..."
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-foreground/80">
            <input type="checkbox" checked={createBoard} onChange={(e) => setCreateBoard(e.target.checked)} />
            Crea board Tasks collegata
          </label>

          {err && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">{err}</div>}
        </div>

        <footer className="flex items-center justify-end gap-2 p-5 border-t border-border shrink-0">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-foreground/80 hover:text-foreground">
            Annulla
          </button>
          <button
            type="submit"
            disabled={submitting || !name.trim()}
            className="px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 rounded-lg text-primary-foreground text-sm font-medium flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Crea
          </button>
        </footer>
      </form>
    </div>
  );
}
